import { memo, ReactElement, ReactNode, useCallback, useState, FocusEvent, useEffect } from "react";

import Collapsible from "src/ui/components/Collapsible";
import useControlled from "src/ui/hooks/useControlled";

import NestedFlatList from "./NestedFlatList";
import TreeNodeView from "./TreeNodeView";
import { TreeNode } from "./types";
import useKeyboardNav from "./useKeyboardNav";

export type TreeViewProps<T extends Record<string, unknown> = Record<string, unknown>> = {
  data: TreeNode<T>[];
  accessibilityLabel: string;
  initialSelectedKey?: string;
  selectedKey?: string;
  onNodePress?: (node: TreeNode<T>) => void;
  onTogglePress?: (node: TreeNode<T>, shouldOpen: boolean) => void;
};

function Tree<T extends Record<string, unknown>>(
  props: TreeViewProps<T>
): ReactElement<TreeViewProps<T>> {
  const { onNodePress, onTogglePress } = props;

  const [openedNodeKeys, setOpenedNodeKeys] = useState<Record<string, boolean>>({});
  const [selectedNodeKey, setSelectedNodeKey] = useControlled({
    controlled: props.selectedKey,
    default: props.initialSelectedKey,
    componentName: "TreeView",
    stateName: "selectedKey",
  });

  const { focusedKey, resetFocusable, handleKeydown } = useKeyboardNav(
    props.data,
    selectedNodeKey,
    setSelectedNodeKey,
    openedNodeKeys,
    setOpenedNodeKeys
  );

  const getSubtreeId = useCallback((node: TreeNode<T>) => `${node.key.toLowerCase()}-subtree`, []);

  // selected key is always visible (path to selected key is opened)
  useEffect(() => {
    if (props.selectedKey) {
      const keysArray = props.selectedKey.slice(1, -1).split("/");
      let openedKeys = {};
      let openedKey = `/`;
      for (let i = 0; i < keysArray.length - 1; i++) {
        openedKey += `${keysArray[i]}/`;
        openedKeys = { ...openedKeys, [openedKey]: true };
      }
      setOpenedNodeKeys((current) => ({ ...current, ...openedKeys }));
    }
  }, [props.selectedKey]);

  if (process.env.NODE_ENV !== "production") {
    // It's conditional on the environment, which will not change once it's set.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      const nodeKeyMap: Record<string, boolean> = {};

      props.data.forEach(checkNodeKey);

      function checkNodeKey(node: TreeNode<T>) {
        if (nodeKeyMap[node.key] === true) {
          console.warn(
            `Key "${node.key}" is duplicated. This can lead to unexpected behaviors. Please provide a unique key for each item in the tree.`
          );
        }

        nodeKeyMap[node.key] = true;

        if (Array.isArray(node.children)) {
          node.children.forEach((node) => checkNodeKey(node));
        }
      }
    }, [props.data]);
  }

  const renderNode = useCallback(
    (node: TreeNode<T>, depth: number) => {
      const nodeHasChildren = Array.isArray(node.children);

      return (
        <TreeNodeView
          nativeID={node.key}
          depth={depth}
          content={node.content}
          isOpen={openedNodeKeys[node.key]}
          isSelected={node.key === selectedNodeKey}
          isFocusable={node.key === focusedKey}
          hasChildren={nodeHasChildren}
          childrenContainerId={nodeHasChildren ? getSubtreeId(node) : undefined}
          onNodePress={() => {
            setSelectedNodeKey(node.key);
            onNodePress && onNodePress(node);
          }}
          onTogglePress={() => {
            const isOpen = openedNodeKeys[node.key] ?? false;
            const shouldOpen = !isOpen;
            setOpenedNodeKeys({
              ...openedNodeKeys,
              [node.key]: shouldOpen,
            });
            onTogglePress && onTogglePress(node, shouldOpen);
          }}
          handleKeydown={handleKeydown}
        />
      );
    },
    [
      openedNodeKeys,
      selectedNodeKey,
      focusedKey,
      getSubtreeId,
      handleKeydown,
      setSelectedNodeKey,
      onNodePress,
      onTogglePress,
    ]
  );

  const renderChildrenContainer = useCallback(
    (node: TreeNode<T>, children: ReactNode) =>
      Array.isArray(node.children) ? (
        <Collapsible
          nativeID={getSubtreeId(node)}
          // @ts-expect-error TS2769: No overload matches this call.
          accessibilityRole="group"
          accessibilityLabel={`${node.content} subtree`}
          isCollapsed={openedNodeKeys[node.key] !== true}
          animationSpeed={200}
        >
          {children}
        </Collapsible>
      ) : null,
    [getSubtreeId, openedNodeKeys]
  );

  return (
    <NestedFlatList
      // @ts-expect-error TS2769: No overload matches this call.
      accessibilityRole="tree"
      accessibilityLabel={props.accessibilityLabel}
      data={props.data}
      renderNode={renderNode}
      renderChildrenContainer={renderChildrenContainer}
      // TS says onBlur is not a valid VirtualList props, but it seems to work just fine for web.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      onBlur={(e: FocusEvent) => {
        const focusLeftTheTree = !e.currentTarget.contains(e.relatedTarget);
        if (focusLeftTheTree) resetFocusable();
      }}
    />
  );
}

/**
 * NOTE: Haven't been tested on mobile devices.
 *
 * Renders a collapsible tree whose "select" and "toggle" are distinct. Uses nested virtual lists
 * under the hood.
 *
 * Features not supported yet:
 * - Multi selection
 * - Custom tree item view
 */
export default memo(Tree) as typeof Tree;
